import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Box, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useGetList } from "react-admin";
import StationInfoSideBar from "./stationInfoSideBar";

interface StationLocation {
  id: string;
  name: string;
  imagePath?: string;
  position: google.maps.LatLngLiteral;
}

const MyMapComponent = React.memo(({
  data,
  center,
  zoom,
  handleMarkerClick,
}: {
  data: StationLocation[];
  center: google.maps.LatLngLiteral;
  zoom: number;
  handleMarkerClick: (stationId: string) => void;
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const mapInstance = new google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapId: import.meta.env.VITE_MAP_ID || 'd06eb9fab6b219cd',
      styles: [
        {
          featureType: 'administrative.country',
          elementType: 'geometry.stroke',
          stylers: [{ visibility: 'on' }, { color: '#888888' }, { weight: 1.5 }]
        },
        {
          featureType: 'administrative.province',
          elementType: 'geometry.stroke',
          stylers: [{ visibility: 'on' }, { color: '#aaaaaa' }, { weight: 0.8 }]
        }
      ]
    });
    mapInstanceRef.current = mapInstance;

    // Fit bounds
    if (data.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      data.forEach(({ position }) => {
        bounds.extend(new google.maps.LatLng(position.lat, position.lng));
      });
      mapInstance.fitBounds(bounds, 50);
    }
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || data.length === 0) return;

    // Limpar marcadores anteriores
    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }

    // Criar marcadores simples (muito mais leve que AdvancedMarkerElement com conteúdo customizado)
    const markers = data.map((station) => {
      const markerContent = document.createElement('div');
      markerContent.className = 'station-marker';
      markerContent.style.cssText = `
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: #4caf50;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
      `;
      markerContent.textContent = station.name.charAt(0).toUpperCase();

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: station.position,
        content: markerContent,
        title: station.name,
      });

      marker.addListener('click', () => {
        handleMarkerClick(station.id);
      });

      return marker;
    });

    markersRef.current = markers;

    // Criar clusterer
    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      renderer: {
        render: ({ count, position }) => {
          const clusterContent = document.createElement('div');
          clusterContent.style.cssText = `
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: #2196f3;
            border: 3px solid white;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 14px;
          `;
          clusterContent.textContent = String(count);

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterContent,
          });
        },
      },
    });

    return () => {
      markersRef.current.forEach(marker => {
        marker.map = null;
      });
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
    };
  }, [data, handleMarkerClick]);

  return (
    <div ref={mapRef} id="map" style={{ height: '100%', width: '100%', overflow: 'hidden' }} />
  );
});

const LoadingScreen: React.FC<{ text?: string }> = ({ text }) => (
  <Grid
    flex={1}
    container
    direction="row"
    justifyContent="center"
    alignItems="center"
    sx={{ bgcolor: grey[100] }}
  >
    <Box>
      <Typography variant="h6">
        {text || 'Carregando'}
      </Typography>
    </Box>
  </Grid>
);

const render = (status: Status): ReactElement => {
  if (status === Status.LOADING) return <LoadingScreen text="Gerando mapa" />;
  return <LoadingScreen />;
};

const MapStations = () => {
  const center = { lat: -15.792487, lng: -47.893526 }; // epicenter of brazil
  const zoom = 4.8;
  const { data: stations, isLoading } = useGetList('stations', {
    pagination: { page: 1, perPage: 9999 },
    sort: { field: 'name', order: 'ASC' }
  });
  const [selectedStation, setSelectedStation] = useState<string | null>(null);
  const [stationInfoSideBar, setStationInfoSideBar] = useState<boolean>(false);

  const toggleDrawer = useCallback((open: boolean) => {
    setStationInfoSideBar(open);
  }, []);

  const stationLocations = useMemo(() => {
    if (!stations) return [];
    return stations
      .filter(station => station.latitude && station.longitude)
      .map(station => ({
        id: station.id,
        imagePath: station.imagePath,
        name: station.name || 'Posto',
        position: { lat: station.latitude, lng: station.longitude }
      }));
  }, [stations]);

  const handleMarkerClick = useCallback((stationId: string) => {
    setSelectedStation(stationId);
    setStationInfoSideBar(true);
  }, []);

  const selectedStationData = useMemo(() => {
    if (!selectedStation || !stations) return null;
    return stations.find(s => s.id === selectedStation);
  }, [selectedStation, stations]);

  return (
    <React.Fragment>
      {selectedStationData && (
        <StationInfoSideBar
          data={selectedStationData}
          isVisible={stationInfoSideBar}
          togglerDrawer={toggleDrawer}
        />
      )}
      {!isLoading && stations ? (
        <Wrapper
          apiKey={import.meta.env.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
          libraries={['marker']}
          render={render}
        >
          <MyMapComponent
            data={stationLocations}
            center={center}
            zoom={zoom}
            handleMarkerClick={handleMarkerClick}
          />
        </Wrapper>
      ) : (
        <LoadingScreen text="Buscando postos" />
      )}
    </React.Fragment>
  );
};

export default React.memo(MapStations);
